import Money from "@dashboard/components/Money";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";
import { CSSProperties, ReactNode, useEffect, useRef } from "react";
import { useIntl } from "react-intl";

import { LabelElement, LabelTextStyle } from "./layout";
import { MarkGraphic } from "./marks";
import { labelFieldLabelMessages } from "./messages";
import { LabelData } from "./types";
import { useFitText } from "./useFitText";

interface LabelFieldContentProps {
  element: LabelElement;
  item: LabelData;
  // The label's physical height, used to express font size as a fraction of
  // it (container-query units) so text scales correctly whether this is
  // rendered in the full-size editor canvas, a shrunk-down preview
  // thumbnail, or the literal-mm print portal — a fixed pt/px size would
  // look right in one and overflow or shrink to nothing in the others.
  heightMm: number;
  widthMm: number;
  // No text on this label prints smaller than this (pt); see textFloorPt.
  minFontSizePt?: number;
}

// 1cqh = 1% of the nearest ancestor with `containerType` set (see
// LabelPreviewCard/LabelCanvasEditor) — here, the label's own physical
// height. Converting pt to a fraction of that height keeps text
// proportional to the label at any render size.
const PT_TO_MM = 25.4 / 72;

// The importer's contact details are the same on every label — a fixed
// company constant, not per-product data — so they're hardcoded here rather
// than stored per-template, same spirit as the brand mark paths in marks.tsx.
const IMPORTER_EMAIL = "geral@maxcei.pt";
const IMPORTER_PHONE = "+351 218 482 671";

const EmailIcon = ({ color }: { color: string }) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ flexShrink: 0 }}>
    <path
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinejoin="round"
      d="M3 5h18v14H3z m0 0 9 7 9-7"
    />
  </svg>
);

const PhoneIcon = ({ color }: { color: string }) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ flexShrink: 0 }}>
    <path
      fill={color}
      d="M6.6 10.8c1.4 2.7 3.7 5 6.4 6.4l2.1-2.1c0.3-0.3 0.7-0.4 1-0.2 1.1 0.4 2.4 0.6 3.6 0.6 0.6 0 1 0.4 1 1v3.6c0 0.6-0.4 1-1 1C10.6 21.1 2.9 13.4 2.9 4.3c0-0.6 0.4-1 1-1h3.6c0.6 0 1 0.4 1 1 0 1.3 0.2 2.5 0.6 3.6 0.1 0.4 0 0.8-0.2 1z"
    />
  </svg>
);

// The template's font size, raised to the label's minimum (see textFloorPt).
const basePt = (style: LabelTextStyle | undefined, minFontSizePt?: number) =>
  Math.max(style?.fontSizePt ?? 9, minFontSizePt ?? 0);

const textStyleToCss = (
  style: LabelTextStyle | undefined,
  heightMm: number,
  minFontSizePt?: number,
): CSSProperties => ({
  // --label-fit is set by FittedText (see useFitText) when the text would
  // overflow its box at the template's size.
  fontSize: `calc(${((basePt(style, minFontSizePt) * PT_TO_MM) / heightMm) * 100}cqh * var(--label-fit, 1))`,
  fontWeight: style?.bold ? "bold" : "normal",
  textAlign: style?.align ?? "left",
  color: style?.color ?? "#000000",
  // Wrap onto the next line between words, but keep explicit line breaks the
  // user typed (pre-wrap does both). Words only break mid-word when even the
  // smallest fitted size can't hold them (--label-wrap, see useFitText).
  whiteSpace: "pre-wrap",
  overflowWrap: "var(--label-wrap, break-word)" as CSSProperties["overflowWrap"],
  lineHeight: 1.25,
  margin: 0,
  width: "100%",
  boxSizing: "border-box",
});

// Wraps a text field so it shrinks to fit `maxHeightPercent` of the label
// instead of being clipped (see useFitText).
const FittedText = ({
  maxHeightPercent,
  contentKey,
  minScale,
  gap,
  children,
}: {
  maxHeightPercent: number;
  contentKey: string;
  minScale?: number;
  gap?: string;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useFitText(ref, maxHeightPercent, contentKey, minScale);

  return (
    <div
      ref={ref}
      style={{ width: "100%", display: "flex", flexDirection: "column", gap, minWidth: 0 }}
    >
      {children}
    </div>
  );
};

// Strips a fixed pixel width/height (sometimes with a stray "px" suffix) off
// a generated SVG and replaces it with a viewBox, so the graphic scales to
// fill an arbitrarily resized box instead of rendering at its native size.
const makeSvgScalable = (svg: SVGSVGElement) => {
  const width = parseFloat(svg.getAttribute("width") ?? "");
  const height = parseFloat(svg.getAttribute("height") ?? "");

  if (width && height) {
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
    svg.removeAttribute("width");
    svg.removeAttribute("height");
  }
};

export const LabelFieldContent = ({
  element,
  item,
  heightMm,
  widthMm,
  minFontSizePt,
}: LabelFieldContentProps) => {
  const intl = useIntl();
  const barcodeRef = useRef<SVGSVGElement>(null);
  const qrCodeRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (element.kind !== "barcode" || !barcodeRef.current || !item.sku) {
      return;
    }

    const svg = barcodeRef.current;

    try {
      JsBarcode(svg, item.sku, {
        format: "CODE128",
        // Keep the bars in their resizable SVG, but render the SKU caption as
        // ordinary text below it. JsBarcode's text is part of the SVG and
        // gets horizontally stretched with preserveAspectRatio="none".
        displayValue: false,
        fontSize: 12,
        margin: 0,
      });
      makeSvgScalable(svg);
    } catch {
      // Invalid SKU characters for Code128 (rare) — leave the barcode blank
      // rather than crash the whole print dialog.
    }
  }, [element.kind, element.barcodeShowText, item.sku]);

  useEffect(() => {
    if (element.kind !== "qrcode" || !qrCodeRef.current) {
      return;
    }

    const svg = qrCodeRef.current;
    let cancelled = false;

    svg.dataset.labelAsset = "pending";

    // The product's storefront page if one is configured, otherwise the SKU
    // — still scannable, just not a link, rather than printing a blank box.
    const value = item.storefrontUrl || item.sku;

    if (!value) {
      svg.dataset.labelAsset = "ready";

      return () => {
        cancelled = true;
      };
    }

    QRCode.toString(value, { type: "svg", margin: 0 })
      .then(markup => {
        if (!qrCodeRef.current || cancelled) {
          return;
        }

        // Copy the generated pattern's viewBox + children into *our* existing
        // <svg> node instead of replacing the element outright (a former
        // `outerHTML = markup` here swapped in the library's own <svg>, which
        // carries no sizing of its own — it then fell back to a square
        // intrinsic size from its viewBox, overflowing any non-square box
        // and getting clipped by the parent's overflow:hidden). Keeping our
        // own element preserves its width/height/preserveAspectRatio, so the
        // QR pattern always scales down to fit its box, never overflows it.
        const parsed = new DOMParser().parseFromString(markup, "image/svg+xml").documentElement;
        const viewBox = parsed.getAttribute("viewBox");

        if (viewBox) {
          svg.setAttribute("viewBox", viewBox);
        }

        svg.replaceChildren(...Array.from(parsed.childNodes).map(node => node.cloneNode(true)));
        svg.dataset.labelAsset = "ready";
      })
      .catch(() => {
        // Leave the box blank rather than crash the whole print dialog.
        if (!cancelled && qrCodeRef.current) {
          qrCodeRef.current.dataset.labelAsset = "ready";
        }
      });

    return () => {
      cancelled = true;
    };
  }, [element.kind, item.storefrontUrl, item.sku]);

  const contentKey = [
    JSON.stringify(element),
    item.name,
    item.sku,
    item.batchNumber,
    item.price?.amount,
    item.price?.currency,
  ].join("|");
  // Fitting may shrink the text, but not below the label's minimum size.
  const minScale = minFontSizePt ? minFontSizePt / basePt(element.style, minFontSizePt) : undefined;
  const fitted = (children: ReactNode) => (
    <FittedText maxHeightPercent={element.box.height} contentKey={contentKey} minScale={minScale}>
      {children}
    </FittedText>
  );

  switch (element.kind) {
    case "image":
      // "contain" (not "cover") — shrinks the whole product photo to fit
      // inside the box rather than cropping it to fill, since these boxes
      // rarely match the photo's own aspect ratio.
      const imageUrl =
        item.imageUrls?.[element.imageIndex ?? 0] ?? (element.imageIndex ? null : item.imageUrl);

      return imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 4 }}
        />
      ) : null;
    case "name":
      return fitted(
        <p style={textStyleToCss(element.style, heightMm, minFontSizePt)}>
          {element.content ?? item.name}
        </p>,
      );
    case "sku":
      return fitted(
        <p style={textStyleToCss(element.style, heightMm, minFontSizePt)}>
          {element.showLabel && intl.formatMessage(labelFieldLabelMessages.skuPrefix)}
          {element.content ?? item.sku}
        </p>,
      );
    case "price":
      return item.price
        ? fitted(
            <p style={textStyleToCss(element.style, heightMm, minFontSizePt)}>
              {element.showLabel && intl.formatMessage(labelFieldLabelMessages.pricePrefix)}
              {element.content ?? <Money money={item.price} />}
            </p>,
          )
        : null;
    case "batch":
      // Hidden (not a "No batch assigned" placeholder) when the variant has
      // no batch code yet and nobody typed an override — an empty batch
      // line on a printed label reads as a mistake, not as information.
      return element.content || item.batchNumber
        ? fitted(
            <p style={textStyleToCss(element.style, heightMm, minFontSizePt)}>
              {element.showLabel && intl.formatMessage(labelFieldLabelMessages.batchPrefix)}
              {element.content ?? item.batchNumber}
            </p>,
          )
        : null;
    case "text":
      return fitted(
        <p style={textStyleToCss(element.style, heightMm, minFontSizePt)}>{element.content}</p>,
      );
    case "importer": {
      const fields = element.importerFields ?? {};
      const textStyle = textStyleToCss(element.style, heightMm, minFontSizePt);
      const rowStyle: CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: "0.35em",
        fontSize: textStyle.fontSize,
        fontWeight: textStyle.fontWeight,
        color: textStyle.color,
        lineHeight: 1,
      };

      return (
        // justifyContent: "flex-end" bottom-anchors the block instead of the
        // default top alignment — the box height is tuned per-template to
        // sit flush above the compliance icons below it, and a box taller
        // than the text (four short address lines rarely fill it exactly)
        // used to leave that leftover space UNDER the text, reading as a
        // stray blank line between the address and the icons.
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            // "safe": when the lines don't fit even at the minimum size, keep
            // the top ("Importador:", company) and cut the last lines instead.
            justifyContent: "safe flex-end",
            height: "100%",
            width: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <FittedText
            maxHeightPercent={element.box.height}
            contentKey={contentKey}
            minScale={minScale}
            gap="0.2em"
          >
            {fields.name !== false && (
              <p style={textStyleToCss(element.style, heightMm, minFontSizePt)}>
                {intl.formatMessage(labelFieldLabelMessages.importerPrefix)}
                {"\n"}
                {element.content}
              </p>
            )}
            {fields.address !== false && (
              <p style={textStyleToCss(element.style, heightMm, minFontSizePt)}>
                {element.importerAddress}
              </p>
            )}
            {fields.email !== false && (
              <div style={rowStyle}>
                <EmailIcon color={String(textStyle.color)} />
                <span>{IMPORTER_EMAIL}</span>
              </div>
            )}
            {fields.phone !== false && (
              <div style={rowStyle}>
                <PhoneIcon color={String(textStyle.color)} />
                <span>{IMPORTER_PHONE}</span>
              </div>
            )}
          </FittedText>
        </div>
      );
    }
    case "barcode":
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <svg
            ref={barcodeRef}
            style={{
              width: "100%",
              height: element.barcodeShowText ? "78%" : "100%",
              flexShrink: 0,
            }}
            preserveAspectRatio="none"
          />
          {element.barcodeShowText && (
            <span
              style={{
                display: "block",
                width: "100%",
                height: "22%",
                flexShrink: 0,
                overflow: "hidden",
                textAlign: "center",
                whiteSpace: "nowrap",
                fontSize: `${Math.max(1.2, Math.min(1.8, (element.box.width * widthMm) / (heightMm * Math.max(1, item.sku?.length ?? 1) * 0.9)))}cqh`,
                lineHeight: 1,
                letterSpacing: "0.12em",
                fontFamily: "monospace",
                color: "#000",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {item.sku}
            </span>
          )}
        </div>
      );
    case "qrcode":
      return (
        <svg
          ref={qrCodeRef}
          data-label-asset="pending"
          style={{ width: "100%", height: "100%" }}
          preserveAspectRatio="xMidYMid meet"
        />
      );
    case "icon":
      return <MarkGraphic markId={element.markId ?? "ce"} color={element.style?.color} />;
    default:
      return null;
  }
};
